import {
  buildHtml,
  buildLink,
  KitchenSinkRoute,
  RouteSlotInput,
} from '@luks/ui';
import { memoize } from 'lodash';

import {
  ListingNewsroomResearchQuery,
  ListingNewsroomResearchQueryVariables,
  LiveNewsTeaserFragment,
  useInfiniteListingNewsroomResearchQuery,
} from '../../../generated/queries';
import { isDefined } from '../../commons/is-defined';
import { Complete } from '../../commons/types';
import { buildDrupalImage } from '../Image';

export type ParagraphNewsroomResearchHeader = {
  __typename: 'ParagraphNewsroomResearchHeader';
};

export const mapParagraphNewsroomResearchHeader = (): RouteSlotInput<
  typeof KitchenSinkRoute,
  'body',
  'SubpageHeader'
> => ({
  key: 'SubpageHeader',
  input: {
    headline: 'Forschung',
    Description: buildHtml(
      'Auf dieser Seite finden Sie ausgewählte Forschungsergebnisse vom Luzerner Kantonsspital.',
    ),
    anchorLink: {
      Link: buildLink({ href: '/newsletter' }),
      title: 'Abonnieren',
    },
  },
});

type GridTeaserSlot = RouteSlotInput<
  typeof KitchenSinkRoute,
  'body',
  'InfiniteGridTeaser'
>;
type GridTeaserSlotInput = Exclude<GridTeaserSlot['input'], Function>;
type GridTeaserItem = GridTeaserSlotInput['teasers'][number];

export const mapParagraphNewsroomResearch = (
  input: ParagraphNewsroomResearchFragment,
  context: { items: Array<any>; index: number },
): RouteSlotInput<typeof KitchenSinkRoute, 'body', 'InfiniteGridTeaser'> => ({
  key: 'InfiniteGridTeaser',
  input: function useListingNewsroomResearch() {
    const pageSize = 5;
    const variables: ListingNewsroomResearchQueryVariables = {
      offset: 0,
      limit: pageSize,
      argument: input.argument,
    };
    const {
      data,
      isLoading,
      isFetching,
      isFetchingNextPage,
      fetchNextPage,
      hasNextPage,
    } = useInfiniteListingNewsroomResearchQuery(variables, {
      keepPreviousData: true,
      getNextPageParam: (lastPage, allPages) => {
        const displayedItems = allPages.reduce(
          (acc, val) => acc + (val.listingNewsroomResearch.items.length || 0),
          0,
        );
        if (lastPage.listingNewsroomResearch.total <= displayedItems) {
          return undefined;
        }
        return {
          ...variables,
          offset: displayedItems,
        };
      },
    });

    const latest = data?.pages.at(-1);

    const mapNewsItem = memoize(
      (item: LiveNewsTeaserFragment): Complete<GridTeaserItem> => {
        return {
          title: item.title,
          date: item.date,
          Link: buildLink({ href: item.path }),
          Description: undefined,
          Image: item.teaserImage
            ? buildDrupalImage(item.teaserImage, 'teaser')
            : undefined,
          arrowIsVisible: false,
          isFullWidth: false,
        };
      },
      (item) => `newsroom-research-news-${item.id}`,
    );

    const aggregateResults = (pages?: Array<ListingNewsroomResearchQuery>) =>
      pages?.reduce(
        (acc, value) => [
          ...acc,
          ...value.listingNewsroomResearch.items
            .filter(isDefined)
            .map(mapNewsItem),
        ],
        [] as Array<GridTeaserItem>,
      ) || [];

    const props: Complete<GridTeaserSlotInput> = {
      teasers: aggregateResults(data?.pages),
      infiniteScroll: context.index === context.items.length - 1,
      MoreLink: hasNextPage
        ? buildLink({
            onClick: (ev) => {
              ev.preventDefault();
              fetchNextPage();
            },
          })
        : undefined,
    };

    const status = isFetchingNextPage
      ? 102
      : isLoading || isFetching
      ? 101
      : data
      ? (latest?.listingNewsroomResearch.total || 0) > 0
        ? 200
        : 404
      : 0;

    return [props, status];
  },
});
